/**
 * Pure copy + formatting for the disclosed-backbone card. Split out so the
 * wording can be tested without mounting anything (same convention as
 * stewardCard-utils.ts).
 */
import type { BackboneProposal, BackboneSource } from "@/lib/types";

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "2026-09-04" -> "Sep 4". Returns the raw string if it isn't a date. */
export function shortDay(d: string | null | undefined): string {
  if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return d ?? "";
  return `${MONTH_SHORT[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;
}

/** "7 nights" for a real span; nothing for a single day. */
export function spanLabel(spanDays: number | undefined): string | null {
  return spanDays && spanDays > 1 ? `${spanDays} nights` : null;
}

/**
 * The headline. Names the CRUISE when there is one, because "your cruise isn't
 * on the itinerary" is the sentence Wade would have written himself — a generic
 * "3 suggestions" is exactly the invisible framing this card exists to fix.
 */
export function backboneHeadline(proposals: BackboneProposal[]): string {
  if (proposals.length === 0) return "";
  const n = proposals.length;
  if (proposals.some((p) => p.key === "cruise")) {
    return n === 1
      ? "Your cruise isn't on the itinerary yet"
      : `Your cruise and ${n - 1} more thing${n - 1 === 1 ? "" : "s"} you told me about aren't on the itinerary yet`;
  }
  return `${n} thing${n === 1 ? "" : "s"} you told me about ${n === 1 ? "isn't" : "aren't"} on the itinerary yet`;
}

const SOURCE_LABEL: Record<BackboneSource, string> = {
  description: "your trip description",
  original_request: "what you first asked for",
  briefing: "the trip briefing",
  link: "the page you linked",
};

/** "Read from your trip description." — the provenance line, always shown. */
export function provenanceLine(proposals: BackboneProposal[]): string {
  const seen = new Set<BackboneSource>();
  for (const p of proposals) for (const s of p.sources) seen.add(s);
  const labels = [...seen].map((s) => SOURCE_LABEL[s]);
  if (labels.length === 0) return "Nothing is added until you say so.";
  return `Read from ${labels.join(" and ")}. Nothing is added until you say so.`;
}

/** The accept-all button's label — always says how many. */
export function addAllLabel(remaining: number): string {
  return remaining === 1 ? "Add it" : `Add all ${remaining}`;
}
